import React from "react";
import { Linkedin, Mail, Twitter } from "lucide-react";
import Footer from "@/components/Footer";
import HeaderPage from "@/components/HeaderPages";

// 1. Define the structure for a team member's data
interface TeamMember {
  id: number;
  name: string;
  title: string;
  bio: string;
  imageUrl: string;
  socials: {
    linkedin?: string;
    twitter?: string;
    email: string;
  };
}

// 2. Mock Data for a Property Valuation Platform Team
const teamMembers: TeamMember[] = [
  {
    id: 1,
    name: "Dr. Evelyn Reed",
    title: "Chief Innovation Officer (CIO)",
    bio: "Dr. Reed is responsible for integrating advanced AI and machine learning models into the valuation engine, ensuring real-time accuracy and performance.",
    imageUrl: "https://placehold.co/400x400/38bdf8/ffffff?text=ER",
    socials: {
      linkedin: "https://linkedin.com/in/evelynreed",
      twitter: "https://twitter.com/evelynreed_ai",
      email: "e.reed@propertytech.com",
    },
  },
  {
    id: 2,
    name: "Marcus Chen",
    title: "Lead Chartered Valuer (FRICS)",
    bio: "With two decades in property appraisal, Marcus anchors our regulatory compliance and oversees the quality assurance of all automated valuation reports.",
    imageUrl: "https://placehold.co/400x400/065f46/ffffff?text=MC",
    socials: {
      linkedin: "https://linkedin.com/in/marcuschen",
      email: "m.chen@propertytech.com",
    },
  },
  {
    id: 3,
    name: "Aisha Khan",
    title: "Head of Geographic Data & Mapping",
    bio: "Aisha specializes in geospatial data analysis, vital for assessing location risk, local infrastructure, and proximity-based valuation adjustments.",
    imageUrl: "https://placehold.co/400x400/9d174d/ffffff?text=AK",
    socials: {
      linkedin: "https://linkedin.com/in/aishakhan_geo",
      twitter: "https://twitter.com/aisha_geo",
      email: "a.khan@propertytech.com",
    },
  },
  {
    id: 4,
    name: "Javier Lopez",
    title: "Director of Platform Engineering",
    bio: "Javier leads the development of our highly scalable cloud infrastructure, ensuring our real-time valuation API is fast, secure, and always available.",
    imageUrl: "https://placehold.co/400x400/f59e0b/ffffff?text=JL",
    socials: {
      linkedin: "https://linkedin.com/in/javierlopez",
      email: "j.lopez@propertytech.com",
    },
  },
];

// 3. Team Member Card Component
const TeamCard: React.FC<{ member: TeamMember }> = ({ member }) => (
  <div className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 p-6 flex flex-col items-center text-center">
    <div className="relative mb-6">
      <img
        className="w-32 h-32 rounded-full object-cover border-4 border-indigo-500/50 transform transition-transform duration-500 hover:scale-105"
        src={member.imageUrl}
        alt={`Profile of ${member.name}`}
        // Fallback for image loading errors
        onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = `https://placehold.co/400x400/9ca3af/ffffff?text=${member.name.substring(
            0,
            1
          )}${member.name.split(" ").pop()?.substring(0, 1) || ""}`;
        }}
      />
    </div>

    <h3 className="text-2xl font-bold text-gray-900 mb-1">{member.name}</h3>
    <p className="text-indigo-600 font-semibold mb-3 text-sm uppercase tracking-wider">
      {member.title}
    </p>

    <p className="text-gray-600 text-sm flex-grow mb-4 leading-relaxed">
      {member.bio}
    </p>

    <div className="flex justify-center space-x-4 pt-3 border-t w-full">
      {member.socials.linkedin && (
        <a
          href={member.socials.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`LinkedIn profile of ${member.name}`}
          className="text-gray-400 hover:text-indigo-600 transition duration-150"
        >
          <Linkedin className="w-6 h-6" />
        </a>
      )}
      {member.socials.twitter && (
        <a
          href={member.socials.twitter}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Twitter profile of ${member.name}`}
          className="text-gray-400 hover:text-indigo-600 transition duration-150"
        >
          <Twitter className="w-6 h-6" />
        </a>
      )}
      <a
        href={`mailto:${member.socials.email}`}
        aria-label={`Email ${member.name}`}
        className="text-gray-400 hover:text-indigo-600 transition duration-150"
      >
        <Mail className="w-6 h-6" />
      </a>
    </div>
  </div>
);

// 4. Main Team Page Component
export default function Team() {
  return (
    <div className="min-h-screen bg-gray-50 font-sans">
    <HeaderPage />
    <div className="min-h-screen bg-gray-50 font-sans">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <header className="text-center py-12 mb-8">
          <p className="text-sm font-semibold text-indigo-600 uppercase tracking-widest mb-2">
            The Architects of Value
          </p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-4">
            Meet Our Leadership Team
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Our team comprises chartered appraisers, AI experts, and seasoned
            engineers dedicated to providing the most accurate, real-time
            property valuation data available.
          </p>
        </header>

        {/* Team Grid Section */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 px-4 sm:px-0">
          {teamMembers.map((member) => (
            <TeamCard key={member.id} member={member} />
          ))}
        </section>

        {/* Call to Action/Mission Statement Footer */}
        {/* <footer className="mt-16 pt-10 border-t border-gray-200 text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Join Our Mission</h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-base">
            We are constantly looking for exceptional talent in property data science and financial technology. Explore career opportunities and help us redefine the future of real estate appraisal.
          </p>
          <button className="mt-6 inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-full shadow-md text-white bg-indigo-600 hover:bg-indigo-700 transition duration-150 transform hover:scale-[1.02]">
            View Open Roles
          </button>
        </footer> */}

        
      </div>
      
    </div>
    <Footer />
    </div>
  );
}
